// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import { For, Show, createEffect, createSignal, onCleanup } from "solid-js";
import Copy from "lucide-solid/icons/copy";
import {
  detectOperatingSystem,
  operatingSystems,
  type OperatingSystem,
} from "./cluster-os";
import { AppLink, useGo } from "./navigation";

const labels: Record<OperatingSystem, string> = {
  macos: "macOS",
  windows: "Windows",
  linux: "Linux",
};

function isOperatingSystem(value: string | undefined): value is OperatingSystem {
  return operatingSystems.includes(value as OperatingSystem);
}

function Snippet(p: { command: string; label: string }) {
  const [copied, setCopied] = createSignal(false);
  let timer: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => clearTimeout(timer));
  const copy = async () => {
    await navigator.clipboard.writeText(p.command.trim());
    setCopied(true);
    clearTimeout(timer);
    timer = setTimeout(() => setCopied(false), 2_000);
  };
  return (
    <div class="command-snippet">
      <button
        type="button"
        aria-label={`Copy ${p.label}`}
        title={copied() ? "Copied!" : "Copy to clipboard"}
        onClick={() => void copy()}
      >
        <Copy size={17} />
      </button>
      <pre>{p.command}</pre>
    </div>
  );
}

function InstallInstructions(p: { os: OperatingSystem }) {
  return (
    <Show
      when={p.os === "macos"}
      fallback={
        <div class="cluster-install-links">
          <p>
            1. Install{" "}
            <a
              data-document-navigation
              href={`https://kubernetes.io/docs/tasks/tools/install-kubectl-${p.os}/`}
              target="_blank"
              rel="noreferrer"
            >
              kubectl
            </a>
            , the Kubernetes official CLI.
          </p>
          <p>
            2. Install the{" "}
            <a
              data-document-navigation
              href="https://github.com/podplane/podplane/releases/latest"
              target="_blank"
              rel="noreferrer"
            >
              Podplane CLI
            </a>
            .
          </p>
          <p>
            3. Install the{" "}
            <a
              data-document-navigation
              href="https://github.com/nadrama-com/cli/releases/latest"
              target="_blank"
              rel="noreferrer"
            >
              Nadrama CLI
            </a>
            .
          </p>
        </div>
      }
    >
      <div class="cluster-install-box">
        <p class="font-semibold">First time using kubectl with your Nadrama login?</p>
        <p>Install kubectl:</p>
        <Snippet command="brew install kubectl" label="kubectl installation command" />
        <p>Install Podplane CLI:</p>
        <Snippet
          command="brew install podplane/tap/podplane"
          label="Podplane installation command"
        />
        <p>Install Nadrama CLI:</p>
        <Snippet
          command="brew install nadrama-com/tap/nadrama"
          label="Nadrama installation command"
        />
      </div>
    </Show>
  );
}

export function ClusterConnect(p: {
  account: string;
  cluster: string;
  name: string;
  os?: string;
  slot: number;
}) {
  const go = useGo();
  const base = `/u${p.slot}/${p.account}/clusters/${p.cluster}/connect`;
  const detected = detectOperatingSystem(navigator.userAgent, navigator.platform);
  const os = () => (isOperatingSystem(p.os) ? p.os : detected);
  const slug = () => p.name.toLowerCase().replace(/\s+/g, "");
  const context = () => (import.meta.env.PROD ? slug() : `dev-${slug()}`);

  createEffect(() => {
    if (!isOperatingSystem(p.os)) go(`${base}/${detected}`, true);
  });

  const commands = () => [
    {
      copy: "Authenticate with this cluster (and automatically configure kubectl context):",
      command: `nadrama cluster login ${p.cluster}`,
      label: "cluster login command",
    },
    {
      copy: "Verify you can connect to your cluster:",
      command: `kubectl --context=${context()} get nodes`,
      label: "cluster verification command",
    },
  ];

  return (
    <section class="cluster-connect">
      <nav aria-label="Operating system" class="cluster-os-tabs">
        <For each={operatingSystems}>
          {(item) => (
            <AppLink
              class={`btn ${os() === item ? "btn-secondary" : ""}`}
              aria-current={os() === item ? "page" : undefined}
              to={`${base}/${item}`}
            >
              {labels[item]}
            </AppLink>
          )}
        </For>
      </nav>
      <h2>Connecting to your cluster from {labels[os()]} using kubectl</h2>
      <InstallInstructions os={os()} />
      <ol class="cluster-connect-steps">
        <For each={commands()}>
          {(item) => (
            <li>
              <p>{item.copy}</p>
              <Snippet command={item.command} label={item.label} />
            </li>
          )}
        </For>
      </ol>
      <h3>What’s Next?</h3>
      <ol class="cluster-connect-steps">
        <li>
          <p>
            Check your Traefik default Let’s Encrypt certificate is ready (this can
            take up to 5 minutes):
          </p>
          <Snippet
            command={`kubectl --context=${context()} get certs -n system-traefik`}
            label="certificate status command"
          />
          <p class="mt-3">If it’s not ready yet, check the ACME challenges:</p>
          <Snippet
            command={`kubectl --context=${context()} get challenges -A`}
            label="ACME challenges command"
          />
        </li>
        <li>
          <p>Check the hello-world pod is running:</p>
          <Snippet
            command={`kubectl --context=${context()} get pods -n default`}
            label="hello-world pod command"
          />
        </li>
        <li>
          <p>Check the Traefik app sync status:</p>
          <Snippet
            command={`kubectl --context=${context()} -n system-platform get applications traefik-app`}
            label="Traefik sync command"
          />
        </li>
        <li>
          Visit your cluster URL:{" "}
          <a
            data-document-navigation
            href={`https://${slug()}.env.cool`}
            target="_blank"
            rel="noreferrer"
          >
            https://{slug()}.env.cool
          </a>
        </li>
      </ol>
    </section>
  );
}
